import { useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { allArticles } from '@/mocks/articles';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = allArticles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-2xl font-bold text-foreground-950">Artigo nao encontrado</h1>
          <p className="mt-2 text-foreground-600">O artigo que procura nao existe ou foi removido.</p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold cursor-pointer"
          >
            Voltar ao Inicio
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        datePublished: article.date,
        dateModified: article.date,
        author: { '@type': 'Organization', name: article.author },
        description: article.excerpt,
        image: article.imageUrl,
        publisher: { '@type': 'Organization', name: 'CSSBuy.watch', url: 'https://cssbuy.watch', logo: { '@type': 'ImageObject', url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png' } },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `https://cssbuy.watch/artigo/${slug}` },
        inLanguage: 'pt-BR',
      },
      ...(article.faq.length > 0 ? [{
        '@type': 'FAQPage',
        mainEntity: article.faq.map((f: { question: string; answer: string }) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: f.answer,
          },
        })),
      }] : []),
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <article>
        <header className="relative w-full pt-24 pb-0">
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6">
              <button onClick={() => navigate('/')} className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                Inicio
              </button>
              <span>/</span>
              <span className="text-foreground-600 truncate">Artigos</span>
              <span>/</span>
              <span className="text-foreground-800 font-medium truncate">{article.title}</span>
            </nav>

            <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-50 text-primary-600 rounded-full mb-4">
              {article.readingTime} de leitura
            </span>

            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-foreground-500">
              <span>{article.author}</span>
              <span className="text-foreground-300">•</span>
              <time dateTime={article.date}>
                {new Date(article.date).toLocaleDateString('pt-BR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </time>
            </div>
          </div>

          <div className="mt-8 w-full max-w-4xl mx-auto px-6 md:px-10">
            <div className="aspect-[16/9] rounded-2xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </header>

        <div className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div
            className="prose prose-lg max-w-none prose-a:text-primary-500 prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground-900 prose-headings:text-foreground-950 prose-headings:font-heading prose-p:text-foreground-700 prose-p:leading-relaxed prose-li:text-foreground-700 prose-img:rounded-xl"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </div>

        {article.faq.length > 0 && (
          <div className="max-w-3xl mx-auto px-6 md:px-10 pb-12">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              Perguntas Frequentes
            </h2>
            <div className="space-y-3">
              {article.faq.map((item, i) => (
                <details key={i} className="group border border-secondary-200 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors">
                    <span className="text-sm font-medium text-foreground-800 pr-4">{item.question}</span>
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:hidden"></i>
                      <i className="ri-arrow-up-s-line text-foreground-400 hidden group-open:block"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {item.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        )}

        <div className="max-w-3xl mx-auto px-6 md:px-10 pb-16">
          <div className="border-t border-secondary-200 pt-8">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              Voltar ao Inicio
            </button>
          </div>
        </div>
      </article>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </div>
  );
}